import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { queryFetch } from '@/composables/utils'
import { v4 as uuidv4 } from 'uuid'
import { useAuthStore } from '@/stores/auth'

export interface ResponseContent {
  chart_type: string
  config: unknown | MetricRendererConfig | ChartConfig
}

export interface ChartConfig {
  charts: {
    title: string
    config: unknown
  }[]
}

interface MapPoint {
  latitude: number
  longitude: number
  title: string
}

type HeatmapPoint = [number, number, number]
export interface MapRendererConfig {
  points?: MapPoint[]
  data?: HeatmapPoint[]
}
export interface MetricRendererConfig {
  label: string
  value: string
}

export interface CellRef {
  kind: 'participant' | 'trainer' | 'course'
  id: number
  variant?: 'participant' | 'trainer'
}

export interface TableRendererConfig {
  headers: { label: string }[]
  rows: { cells: { cell: string | number; ref?: CellRef }[] }[]
}

export interface DonutRendererConfig {
  data: { category: string; value: number }[]
  categoryLabel: string
  valueLabel: string
}

export interface MultiLineRendererConfig {
  series: { name: string; data: { date: string; value: number }[] }[]
  xAxisLabel: string
  yAxisLabel: string
}
export interface UserMessage {
  type: 'user'
  content: string
}

export interface ResponseMessage {
  type: 'response'
  content: ResponseContent
}

export type Message = UserMessage | ResponseMessage

interface Chat {
  id: string
  title: string
  messages: Message[]
  lastQuery: string
  tokens: {
    input: number
    output: number
    total: number
  }
}

import { STORAGE_KEY_CHAT_CURRENT_ID, STORAGE_KEY_CHAT_HISTORY } from '@/lib/storage'

const CHAT_HISTORY_STORAGE_KEY = STORAGE_KEY_CHAT_HISTORY
const CURRENT_CHAT_ID_STORAGE_KEY = STORAGE_KEY_CHAT_CURRENT_ID

export const useQueryChatStore = defineStore('queryChat', () => {
  const chatHistory = ref<Chat[]>([])
  const currentChatId = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const showSessionAlert = ref(false)

  const currentChat = computed(() => {
    return chatHistory.value.find((chat) => chat.id === currentChatId.value) || null
  })

  const loadChatFromLocalStorage = () => {
    const savedHistory = localStorage.getItem(CHAT_HISTORY_STORAGE_KEY)
    const savedChatId = localStorage.getItem(CURRENT_CHAT_ID_STORAGE_KEY)

    if (savedHistory) {
      chatHistory.value = JSON.parse(savedHistory)
    }
    if (savedChatId) {
      currentChatId.value = JSON.parse(savedChatId)
    }

    if (!currentChat.value && chatHistory.value.length > 0) {
      currentChatId.value = chatHistory.value[0].id
    }

    if (!currentChat.value) {
      showSessionAlert.value = true
    }
  }

  // Generic debounce to avoid 'any'
  const debounce = <T extends unknown[]>(func: (...args: T) => void, delay: number) => {
    let timeoutId: number | undefined
    return (...args: T) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = window.setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const debouncedSave = debounce(() => {
    localStorage.setItem(CHAT_HISTORY_STORAGE_KEY, JSON.stringify(chatHistory.value))
    localStorage.setItem(CURRENT_CHAT_ID_STORAGE_KEY, JSON.stringify(currentChatId.value))
  }, 300)

  watch(
    [chatHistory, currentChatId],
    () => {
      debouncedSave()
    },
    { deep: true },
  )

  const startNewChat = () => {
    const newChat: Chat = {
      id: uuidv4(),
      title: 'New Chat',
      messages: [],
      lastQuery: '',
      tokens: { input: 0, output: 0, total: 0 },
    }
    chatHistory.value.unshift(newChat)
    currentChatId.value = newChat.id
    showSessionAlert.value = true
  }

  const sendMessage = async (userRequest: string) => {
    if (!currentChat.value) {
      startNewChat()
    }
    const chat = currentChat.value!
    showSessionAlert.value = false

    const auth = useAuthStore()
    const username = auth.user?.username

    loading.value = true
    error.value = null
    chat.messages.push({ type: 'user', content: userRequest })

    if (chat.messages.length === 1 && chat.title === 'New Chat') {
      chat.title = userRequest.length > 30 ? `${userRequest.substring(0, 30)}...` : userRequest
    }

    try {
      const response = await queryFetch('/nlp/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: username,
          user_request: userRequest,
          last_query: chat.lastQuery,
        }),
      })

      if (!response.ok) throw new Error(response.statusText)
      const data = await response.json()

      chat.lastQuery = data.query
      chat.tokens.input += data.input_tokens
      chat.tokens.output += data.output_tokens
      chat.tokens.total = chat.tokens.input + chat.tokens.output

      let config = data.config
      if (typeof config === 'string' && data.chart_type !== 'table') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.error('Failed to parse chart config:', e)
          throw new Error('Invalid chart configuration received from server.')
        }
      }

      const responseContent: ResponseContent = {
        chart_type: data.chart_type,
        config: config,
      }

      chat.messages.push({ type: 'response', content: responseContent })
    } catch (e: unknown) {
      if (e instanceof Error) error.value = e.message || 'An unknown error occurred'
      console.error('Send message error:', e)
      chat.messages.push({
        type: 'response',
        content: { chart_type: 'error', config: error.value },
      })
    } finally {
      loading.value = false
    }
  }

  const selectChat = (chatId: string) => {
    currentChatId.value = chatId
  }

  const deleteChat = (chatId: string) => {
    const index = chatHistory.value.findIndex((chat) => chat.id === chatId)
    if (index !== -1) {
      chatHistory.value.splice(index, 1)
      if (currentChatId.value === chatId) {
        currentChatId.value = chatHistory.value.length > 0 ? chatHistory.value[0].id : null
      }
    }
  }

  /** Reset all in-memory state and remove persisted data. Called on logout. */
  const clearPersistedState = () => {
    chatHistory.value = []
    currentChatId.value = null
    loading.value = false
    error.value = null
    showSessionAlert.value = false
    localStorage.removeItem(CHAT_HISTORY_STORAGE_KEY)
    localStorage.removeItem(CURRENT_CHAT_ID_STORAGE_KEY)
  }

  return {
    chatHistory,
    currentChatId,
    currentChat,
    loading,
    error,
    showSessionAlert,
    loadChatFromLocalStorage,
    startNewChat,
    sendMessage,
    selectChat,
    deleteChat,
    clearPersistedState,
  }
})
